import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";
import { useDraggableCard } from "../../hooks/useDraggableCard";
import { ReviewGroup } from "./fieldReviewLogic";

// Approve/deny walkthrough for the AI's field suggestions, styled like the
// report editor's suggestions review: a floating card, "1 of 5", one group at a
// time, Discard / Accept, and an X that asks what to do with the rest. The
// designer shows the group on screen live (the parent applies `onPreview`), and
// nothing is saved until the review ends.
interface Props {
  groups: ReviewGroup[];
  // The groups approved so far plus the one on screen, to preview on the PDF.
  onPreview: (accepted: Set<string>, current: ReviewGroup) => void;
  // Review is over: save exactly the approved groups.
  onFinish: (accepted: Set<string>) => void;
  // Throw everything away and restore the form as it was.
  onDiscardAll: () => void;
  // Wheel events over the blocking layer scroll the PDF underneath.
  onWheelPassthrough?: (e: React.WheelEvent) => void;
  // Where to render. Defaults to <body>; inside a modal <dialog> (top layer) the
  // card has to live in the dialog or it would sit behind it.
  portalContainer?: HTMLElement | null;
}

const MAX_ITEMS = 8;

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block max-w-full truncate rounded-full bg-base-200 dark:bg-base-300 px-2 py-0.5 text-xs text-base-content/80">
      {children}
    </span>
  );
}

export default function AiFieldReview({ groups, onPreview, onFinish, onDiscardAll, onWheelPassthrough, portalContainer }: Props) {
  const [index, setIndex] = useState(0);
  const [accepted, setAccepted] = useState<Set<string>>(new Set());
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const drag = useDraggableCard();
  const previewRef = useRef(onPreview);
  previewRef.current = onPreview;

  const current = groups[index];

  // Show the step on screen: approved so far, plus this one.
  useEffect(() => {
    if (!current) return;
    previewRef.current(new Set([...accepted, current.key]), current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const decide = (accept: boolean) => {
    const next = new Set(accepted);
    if (accept) next.add(current.key);
    setAccepted(next);
    if (index + 1 >= groups.length) onFinish(next);
    else setIndex(index + 1);
  };

  const acceptRemaining = () => {
    const next = new Set(accepted);
    groups.slice(index).forEach((g) => next.add(g.key));
    onFinish(next);
  };

  if (!current) return null;
  const hasAccepted = accepted.size > 0;

  return createPortal(
    <>
      {/* Keeps the form read-only while reviewing; scrolling still reaches the PDF. */}
      <div className="fixed inset-0 z-[9998]" onWheel={onWheelPassthrough} />
      <div className="fixed z-[9999] pointer-events-none" style={drag.wrapperStyle}>
        <div
          ref={drag.cardRef}
          className="pointer-events-auto flex flex-col max-h-[85vh] w-[520px] max-w-[90vw] bg-base-100 rounded-xl shadow-2xl border border-base-300 overflow-hidden animate-[fadeSlideUp_0.25s_ease-out]"
        >
          <div
            className="shrink-0 flex items-center justify-between px-5 py-3 border-b border-base-200 bg-base-200/80 cursor-grab active:cursor-grabbing select-none"
            onMouseDown={drag.handleDragStart}
          >
            <div className="flex items-center gap-3">
              <h4 className="text-sm font-semibold text-gray-800 dark:text-base-content/80 m-0">Review Suggested Fields</h4>
              <span className="text-xs font-medium text-base-content/40 bg-gray-100 dark:bg-base-300 px-2 py-0.5 rounded-full">
                {index + 1} of {groups.length}
              </span>
            </div>
            <button
              onClick={() => setShowStopConfirm(true)}
              className="text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 transition-colors"
              title="Stop reviewing"
            >
              <XMarkIcon className="size-5" />
            </button>
          </div>

          {showStopConfirm ? (
            <div className="px-5 py-4 bg-amber-50/50 dark:bg-amber-950/40">
              <p className="text-sm text-gray-700 dark:text-base-content/80 mb-3 m-0">
                {hasAccepted
                  ? `You've accepted ${accepted.size} of ${groups.length} groups. What would you like to do with the rest?`
                  : "Are you sure you want to stop reviewing? None of the suggestions will be applied."}
              </p>
              <div className="flex items-center gap-2">
                {hasAccepted && (
                  <Button variant="primary" size="xs" onClick={() => onFinish(accepted)}>
                    Keep Accepted
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="xs"
                  className="!border-red-200 dark:!border-red-900 !text-red-600 dark:!text-red-400 hover:!bg-red-50 dark:hover:!bg-red-950/40"
                  onClick={onDiscardAll}
                >
                  {hasAccepted ? "Discard All" : "Discard"}
                </Button>
                <button
                  onClick={() => setShowStopConfirm(false)}
                  className="text-xs text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 ml-auto"
                >
                  Continue reviewing
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="px-5 py-4 flex-1 overflow-y-auto min-h-0 space-y-3">
                <div>
                  <p className="text-xs text-base-content/40 mb-0.5 m-0">Section</p>
                  <p className="text-sm font-medium text-gray-700 dark:text-base-content/80 m-0">{current.title}</p>
                  {current.context && (
                    <p className="text-xs text-base-content/60 mt-1 m-0">{current.context}</p>
                  )}
                </div>

                {current.added.length > 0 && (
                  <div>
                    <p className="text-xs text-base-content/40 mb-1 m-0">
                      Adds {current.added.length} new field{current.added.length === 1 ? "" : "s"} (selected on the form)
                    </p>
                    <div className="bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900 rounded-lg px-3 py-2 flex flex-wrap gap-1.5">
                      {current.added.slice(0, MAX_ITEMS).map((f) => (
                        <Chip key={f.id}>{f.type === "circle" ? `${f.label} (circle ${f.multiple ? "all that apply" : "one"}, ${f.options?.length ?? 0} options)` : f.label}</Chip>
                      ))}
                      {current.added.length > MAX_ITEMS && <Chip>+{current.added.length - MAX_ITEMS} more</Chip>}
                    </div>
                  </div>
                )}

                {current.changed.length > 0 && (
                  <div>
                    <p className="text-xs text-base-content/40 mb-1 m-0">
                      Changes {current.changed.length} existing field{current.changed.length === 1 ? "" : "s"}
                    </p>
                    <ul className="bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-900 rounded-lg px-3 py-2 m-0 list-none space-y-1 text-xs text-gray-700 dark:text-base-content/80">
                      {current.changed.slice(0, MAX_ITEMS).map((c) => (
                        <li key={c.after.id}>
                          <span className="font-medium">{c.before.label}</span>: {c.notes.join("; ")}
                        </li>
                      ))}
                      {current.changed.length > MAX_ITEMS && (
                        <li className="text-base-content/60">+{current.changed.length - MAX_ITEMS} more</li>
                      )}
                    </ul>
                  </div>
                )}

                {current.flagged.length > 0 && (
                  <p className="text-xs text-amber-700 dark:text-amber-400 m-0">
                    {current.flagged.length} existing field{current.flagged.length === 1 ? "" : "s"} here may not sit on a
                    visible input ({current.flagged.slice(0, 3).map((f) => f.label).join(", ")}
                    {current.flagged.length > 3 ? ", ..." : ""}). They are kept as they are; worth a look.
                  </p>
                )}
              </div>

              <div className="shrink-0 flex items-center gap-2 px-5 py-3 border-t border-base-200 bg-base-200/50">
                {groups.length - index > 1 && (
                  <button
                    onClick={acceptRemaining}
                    className="text-xs text-base-content/50 hover:text-blue-600 dark:hover:text-blue-300 mr-auto"
                  >
                    Accept all remaining
                  </button>
                )}
                <Button variant="outline" size="sm" className="ml-auto" onClick={() => decide(false)}>
                  Discard
                </Button>
                <Button variant="primary" size="sm" onClick={() => decide(true)}>
                  Accept
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>,
    portalContainer ?? document.body,
  );
}
